import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, Wand2, Image, Save, Send, ArrowLeft, Loader2,
  Eye, Edit3, Sparkles, CheckCircle, PenTool, Search, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

const CATEGORIES = [
  { value: 'inteligencia-artificial', label: 'Inteligencia Artificial' },
  { value: 'software', label: 'Software & Apps' },
  { value: 'hardware', label: 'Hardware & Gadgets' },
  { value: 'startups', label: 'Startups & Emprendimiento' },
  { value: 'ciberseguridad', label: 'Ciberseguridad' },
  { value: 'redes-sociales', label: 'Redes Sociales' },
  { value: 'gaming', label: 'Gaming & eSports' },
  { value: 'ciencia', label: 'Ciencia & Innovación' },
  { value: 'actualidad', label: 'Actualidad Tech' },
];

const EditorWritePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [mode, setMode] = useState(null); // null = choose, 'ai' = AI mode, 'manual' = manual mode
  const [loading, setLoading] = useState(false);
  const [improving, setImproving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // AI mode state
  const [aiTopic, setAiTopic] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');
  const [useFirecrawl, setUseFirecrawl] = useState(true);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  
  // Manual/Edit mode state
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: 'actualidad',
    summary: '',
    author: ''
  });
  const [coverImage, setCoverImage] = useState('');
  const [aiEditInstructions, setAiEditInstructions] = useState('');

  useEffect(() => {
    if (authLoading) return;
    const isStaffOrAdmin = user && (
      user.user_type === 'admin' || user.user_type === 'staff' ||
      ['admin', 'editor', 'disenador'].includes(user.staff_role)
    );
    if (!isStaffOrAdmin) { navigate('/'); return; }
    setForm(prev => ({ ...prev, author: user.name || '' }));
  }, [user, authLoading, navigate]);

  // Poll AI task
  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const status = await api.getAITaskStatus(taskId);
        setTaskStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          setLoading(false);
          if (status.status === 'completed' && status.result?.dossier_id) {
            setSuccess('Artículo generado como borrador. Redirigiendo...');
            setTimeout(() => navigate('/editor/my-content'), 2000);
          }
        }
      } catch { clearInterval(interval); setLoading(false); }
    }, 3000);
    return () => clearInterval(interval);
  }, [taskId, navigate]);

  // AI: Generate article
  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) { setError('Escribe un tema'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.createArticleFromTopic(aiTopic.trim(), aiInstructions.trim(), useFirecrawl);
      if (res.success) {
        setTaskId(res.task_id);
        setTaskStatus({ status: 'processing', progress: 0 });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear artículo');
      setLoading(false);
    }
  };

  // Manual: Improve with AI
  const handleImproveText = async () => {
    if (!form.title.trim() || !form.content.trim()) { setError('Escribe título y contenido primero'); return; }
    setImproving(true); setError('');
    try {
      const response = await api.improveArticleText({
        title: form.title, content: form.content, category: form.category
      });
      setForm(prev => ({
        ...prev,
        title: response.improved_title || prev.title,
        content: response.improved_content || prev.content,
        summary: response.summary || prev.summary
      }));
      setSuccess('Texto mejorado con IA');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al mejorar el texto');
    } finally { setImproving(false); }
  };

  // AI Edit with custom instructions
  const handleAIEdit = async () => {
    if (!aiEditInstructions.trim()) { setError('Escribe instrucciones para la IA'); return; }
    setImproving(true); setError('');
    try {
      const response = await api.improveArticleText({
        title: form.title, content: form.content, category: form.category,
        instructions: aiEditInstructions
      });
      setForm(prev => ({
        ...prev,
        title: response.improved_title || prev.title,
        content: response.improved_content || prev.content,
        summary: response.summary || prev.summary
      }));
      setAiEditInstructions('');
      setSuccess('Artículo editado con IA');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al editar');
    } finally { setImproving(false); }
  };

  // Generate image
  const handleGenerateImage = async () => {
    setGeneratingImage(true); setError('');
    try {
      const response = await api.generateArticleImage({
        title: form.title, content: form.content, category: form.category
      });
      setCoverImage(response.image_url || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar imagen');
    } finally { setGeneratingImage(false); }
  };

  // Publish/Save
  const handlePublish = async (asDraft = false) => {
    setLoading(true); setError('');
    try {
      await api.createArticle({
        title: form.title, content: form.content, summary: form.summary,
        cover_image: coverImage, category: form.category, author: form.author,
        status: asDraft ? 'draft' : 'active'
      });
      setSuccess(asDraft ? 'Guardado como borrador' : 'Publicado exitosamente');
      setTimeout(() => navigate('/editor/my-content'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally { setLoading(false); }
  };

  if (authLoading) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="editor-write-page">
      {/* Header */}
      <div className="bg-black text-white py-5">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <Link to="/editor/my-content" className="text-gray-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-xl font-bold">Nueva Noticia</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Alerts */}
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{success}</div>}

        {/* ─── MODE SELECTION ─── */}
        {mode === null && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="mode-selection">
            <button
              onClick={() => setMode('ai')}
              className="bg-white rounded-xl border-2 border-gray-200 hover:border-black p-8 text-left transition-colors group"
              data-testid="mode-ai"
            >
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2">Generar con IA</h3>
              <p className="text-sm text-[#555]">
                Escribe un tema y la IA investigará fuentes con Firecrawl, redactará el artículo completo y generará la imagen.
              </p>
            </button>
            
            <button
              onClick={() => setMode('manual')}
              className="bg-white rounded-xl border-2 border-gray-200 hover:border-black p-8 text-left transition-colors group"
              data-testid="mode-manual"
            >
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <PenTool className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2">Escribir Manual</h3>
              <p className="text-sm text-[#555]">
                Escribe tu artículo manualmente. Puedes usar la IA para mejorar, editar y generar imágenes en cualquier momento.
              </p>
            </button>
          </div>
        )}

        {/* ─── AI MODE ─── */}
        {mode === 'ai' && (
          <div className="space-y-6">
            <button onClick={() => { setMode(null); setTaskId(null); setTaskStatus(null); }} className="text-sm text-[#555] hover:text-black flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Cambiar modo
            </button>

            <div className="bg-white rounded-xl border p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Generar con IA
              </h2>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Tema o titular de la noticia</label>
                <Input
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="Ej: Apple lanza nuevo chip M5 con rendimiento revolucionario"
                  className="text-base"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Instrucciones editoriales (opcional)</label>
                <Textarea
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  placeholder="Ej: Enfocarse en el impacto para consumidores..."
                  rows={3}
                  disabled={loading}
                />
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <button
                  onClick={() => setUseFirecrawl(!useFirecrawl)}
                  className={`w-10 h-6 rounded-full transition-colors flex items-center ${useFirecrawl ? 'bg-black justify-end' : 'bg-gray-300 justify-start'}`}
                >
                  <div className="w-5 h-5 bg-white rounded-full mx-0.5 shadow" />
                </button>
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Globe className="w-4 h-4" /> Investigar con Firecrawl
                  </p>
                  <p className="text-xs text-[#555]">Busca fuentes web para un artículo más completo y con datos reales</p>
                </div>
              </div>

              <Button onClick={handleAIGenerate} disabled={loading || !aiTopic.trim()} className="bg-black text-white hover:bg-gray-800 w-full">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {loading ? 'Generando artículo...' : (useFirecrawl ? 'Investigar y Generar Artículo' : 'Generar Artículo')}
              </Button>

              {taskStatus && (
                <div className={`p-4 rounded-lg border ${taskStatus.status === 'completed' ? 'bg-green-50 border-green-200' : taskStatus.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                  {taskStatus.status === 'processing' && (
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">Investigando y generando...</p>
                        <p className="text-xs text-blue-600 mt-1">La IA busca fuentes y redacta el artículo</p>
                        <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                          <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${taskStatus.progress || 10}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                  {taskStatus.status === 'completed' && (
                    <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Artículo creado como borrador
                    </p>
                  )}
                  {taskStatus.status === 'failed' && (
                    <p className="text-sm text-red-700">Error: {taskStatus.error}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── MANUAL MODE ─── */}
        {mode === 'manual' && (
          <div className="space-y-6">
            <button onClick={() => setMode(null)} className="text-sm text-[#555] hover:text-black flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Cambiar modo
            </button>

            <div className="bg-white rounded-xl border p-6 space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <PenTool className="w-5 h-5" /> Escribir Noticia
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-2">Titular</label>
                  <Input value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Titular de la noticia" className="text-base" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Categoría</label>
                  <Select value={form.category} onValueChange={(v) => setForm(p => ({...p, category: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Autor</label>
                  <Input value={form.author} onChange={(e) => setForm(p => ({...p, author: e.target.value}))} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Contenido</label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm(p => ({...p, content: e.target.value}))}
                  placeholder="Escribe el contenido de la noticia..."
                  rows={12}
                  className="text-sm leading-relaxed"
                />
                <p className="text-xs text-[#555] mt-1">{form.content.length} caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Resumen (opcional)</label>
                <Textarea value={form.summary} onChange={(e) => setForm(p => ({...p, summary: e.target.value}))} placeholder="Breve resumen..." rows={2} />
              </div>
            </div>

            {/* AI Editing Tools */}
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <Wand2 className="w-4 h-4" /> Herramientas de IA
              </h3>
              
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleImproveText} disabled={improving || !form.content.trim()} variant="outline" size="sm">
                  {improving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Mejorar texto con IA
                </Button>
                <Button onClick={handleGenerateImage} disabled={generatingImage || !form.title.trim()} variant="outline" size="sm">
                  {generatingImage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Image className="w-4 h-4 mr-1" />}
                  Generar imagen
                </Button>
              </div>

              <div className="flex gap-2">
                <Input
                  value={aiEditInstructions}
                  onChange={(e) => setAiEditInstructions(e.target.value)}
                  placeholder="Instrucciones para la IA: ej. 'Hazlo más técnico', 'Agrega datos'..."
                  className="text-sm"
                />
                <Button onClick={handleAIEdit} disabled={improving || !aiEditInstructions.trim()} size="sm" className="bg-black text-white hover:bg-gray-800 whitespace-nowrap">
                  {improving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Edit3 className="w-4 h-4 mr-1" />}
                  Editar con IA
                </Button>
              </div>

              {coverImage && (
                <div className="mt-3">
                  <p className="text-xs font-semibold mb-2">Imagen generada:</p>
                  <img src={coverImage.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${coverImage}` : coverImage} alt="" className="w-full max-w-md rounded-lg" />
                </div>
              )}
            </div>

            {/* Publish Actions */}
            <div className="flex gap-3">
              <Button onClick={() => handlePublish(true)} disabled={loading || !form.title.trim()} variant="outline">
                <Save className="w-4 h-4 mr-2" />
                Guardar borrador
              </Button>
              <Button onClick={() => handlePublish(false)} disabled={loading || !form.title.trim() || !form.content.trim()} className="bg-black text-white hover:bg-gray-800">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Publicar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorWritePage;
